import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, CheckCircle2, AlertCircle, Clock } from "lucide-react";

export default function PaymentSuccess() {
  const { adoptToken } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", message: "", name: "" });

  const confirm = useCallback(async () => {
    const pid = localStorage.getItem("pending_payment_id") ||
      new URLSearchParams(window.location.search).get("id");
    if (!pid) {
      setState({ status: "error", message: "Keine Zahlung gefunden. Bitte starten Sie die Buchung erneut." });
      return;
    }
    setState((s) => ({ ...s, status: "checking" }));
    try {
      const { data } = await api.post(`/payments/${pid}/confirm`, {});
      localStorage.removeItem("pending_payment_id");
      if (data.token) await adoptToken(data.token);
      setState({ status: "success", message: "", name: data.practice_name || "" });
      setTimeout(() => navigate("/admin", { replace: true }), 2200);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "";
      if (detail.includes("noch nicht abgeschlossen") || err.response?.status === 400) {
        setState({ status: "pending", message: detail });
      } else {
        setState({ status: "error", message: detail || "Bestätigung fehlgeschlagen." });
      }
    }
  }, [adoptToken, navigate]);

  useEffect(() => { confirm(); }, [confirm]);

  return (
    <div className="min-h-screen bg-emerald-950 grid place-items-center px-6 py-10" data-testid="payment-success-page">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 md:p-10 text-center">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-emerald-700 animate-spin mx-auto mb-5" />
            <h1 className="font-display text-2xl font-semibold text-emerald-900">Zahlung wird geprüft…</h1>
            <p className="text-stone-500 mt-2">Einen Moment bitte.</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 data-testid="payment-success-title" className="font-display text-2xl font-semibold text-emerald-900">Zahlung erfolgreich!</h1>
            <p className="text-stone-500 mt-2">Ihr Zugang ist jetzt vollständig freigeschaltet{state.name ? ` – willkommen, ${state.name}` : ""}. Sie werden weitergeleitet…</p>
            <Button onClick={() => navigate("/admin", { replace: true })} className="mt-6 h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white w-full">
              Zum Dashboard
            </Button>
          </>
        )}
        {state.status === "pending" && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 grid place-items-center mx-auto mb-5">
              <Clock className="w-7 h-7" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-emerald-900">Zahlung wird verarbeitet</h1>
            <p className="text-stone-500 mt-2 mb-6">{state.message || "Ihre Zahlung ist noch nicht abgeschlossen."} Sobald sie bestätigt ist, schalten wir Ihren Zugang frei.</p>
            <Button onClick={confirm} data-testid="payment-recheck" className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white w-full">
              Status erneut prüfen
            </Button>
          </>
        )}
        {state.status === "error" && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-rose-100 text-rose-600 grid place-items-center mx-auto mb-5">
              <AlertCircle className="w-7 h-7" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-emerald-900">Etwas ist schiefgelaufen</h1>
            <p className="text-stone-500 mt-2 mb-6">{state.message}</p>
            <Button onClick={() => navigate("/bezahlen", { replace: true })} className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white w-full">
              Erneut versuchen
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
